import {useEffect, useMemo, useState} from 'react';
import MultiSelectDropdown from '../../../../../components/form/MultiSelectDropdown';
import InlineMultiSelect from '../../../../../components/form/InlineMultiSelect';
import {DocumentFilterBarProps, FilterOption} from './partnerDocuments';
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {FilterPendingBadge} from "../../../../../components/general/FilterPendingBadge.tsx";
import {fieldVariants, fieldLabelClasses} from "../../../../../components/general/fieldStyles.ts";
import {cn} from "../../../../../lib/utils.ts";
import Dropdown from "../../../../../components/general/Dropdown.tsx";
import {ChevronDownIcon, Cog6ToothIcon} from "@heroicons/react/24/outline";
import Popup from '../../../../../components/general/Popup.tsx';
import { DateRangeInput } from '../../../../../components/general/DateRangeInput';
import {useFilterStore} from "../../../../../contexts/FilterStore.tsx";
import {formatDateForInput, parseDate} from "../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts";
import {
    DOCUMENT_TYPE_FILTER_OPTIONS,
    DocumentTypeFilterEnum,
    fromCategoryAndDirection,
    toCategoryAndDirection,
} from './partnerDocumentListFilterMap';
import type {DateFilterType, FilterTags} from "../../../../../generated/enums.ts";

export default function DocumentFilterBar({
                                              availableRelations,
                                              availableTags,
                                          }: DocumentFilterBarProps) {
    const { state, dispatch } = useFilterStore();

    const [dateFrom, setDateFrom] = useState(state.common.dateRange.from);
    const [dateTo, setDateTo] = useState(state.common.dateRange.to);
    const [dateType, setDateType] = useState<DateFilterType>(state.documents.dateType);
    const [isDateTypePopupOpen, setIsDateTypePopupOpen] = useState(false);
    const selectedClientId = state.common.customerId;
    const [selectedRelations, setSelectedRelations] = useState<string[]>(state.documents.relations || []);
    const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentTypeFilterEnum>(
        fromCategoryAndDirection(state.documents.documentDirection, state.documents.documentCategories || []),
    );
    const [selectedTags, setSelectedTags] = useState<string[]>(state.documents.tags || []);
    const [selectedTagsMode, setSelectedTagsMode] = useState<FilterTags>(state.documents.tagsMode);
    const [isDirty, setIsDirty] = useState(false);

    const dateRange = useMemo(() => ({
        from: parseDate(dateFrom),
        to: parseDate(dateTo)
    }), [dateFrom, dateTo]);

    const handleDateRangeChange = (range: { from: Date | null, to: Date | null }) => {
        setIsDirty(true);
        setDateFrom(formatDateForInput(range.from));
        setDateTo(formatDateForInput(range.to));
    };

    useEffect(() => {
        setDateFrom(state.common.dateRange.from);
        setDateTo(state.common.dateRange.to);
        setDateType(state.documents.dateType);
        setSelectedRelations(state.documents.relations || []);
        setSelectedDocumentType(
            fromCategoryAndDirection(state.documents.documentDirection, state.documents.documentCategories || []),
        );
        setSelectedTags(state.documents.tags || []);
        setSelectedTagsMode(state.documents.tagsMode);
        setIsDirty(false);
    }, [state]);

    const allRelationNames = useMemo(
        () =>
            Array.from(
                new Set(availableRelations.map((relation) => relation.name))
            ).sort(),
        [availableRelations]
    );

    const filteredRelations = useMemo(() => {
        if (!selectedClientId) {
            return allRelationNames;
        }

        const namesForSelectedBusinesses = new Set(
            availableRelations
                .filter(
                    (relation) =>
                        relation.business_id &&
                        selectedClientId === relation.business_id
                )
                .map((relation) => relation.name)
        );

        const filtered = allRelationNames.filter((name) =>
            namesForSelectedBusinesses.has(name)
        );

        return filtered.length ? filtered : allRelationNames;
    }, [selectedClientId, availableRelations, allRelationNames]);

    useEffect(() => {
        setSelectedRelations((prev) => prev?.filter((relation) => filteredRelations.includes(relation)) || []);
    }, [filteredRelations]);

    const handleSubmit = () => {
        setIsDirty(false);

        const docFilters = toCategoryAndDirection(selectedDocumentType);

        dispatch({
            type: 'SET_FILTERS',
            payload: {
                common: {
                    dateRange: {
                        from: dateFrom,
                        to: dateTo
                    },
                },
                documents: {
                    dateType,
                    relations: selectedRelations,
                    documentDirection: docFilters.documentDirection,
                    documentCategories: docFilters.documentCategories,
                    tags: selectedTags,
                    tagsMode: selectedTagsMode,
                }
            }
        });
    };

    const handleClear = () => {
        setIsDirty(false);
        dispatch({ type: 'RESET_DOCUMENT_FILTERS' });
    };

    const handleDateTypeChange = (type: DateFilterType) => {
        setIsDirty(true);
        setDateType(type);
        setIsDateTypePopupOpen(false);
    };
    const hideInvoiceDateTypePicker = selectedDocumentType === DocumentTypeFilterEnum.CREDIT_CARD;

    return (
        <div className="mb-4 bg-white justify-between w-full flex items-end">
            <div className="flex items-end flex-wrap gap-4">
                <div>
                    <div className="flex items-center mb-2">
                        {hideInvoiceDateTypePicker ? (
                            <span className={fieldLabelClasses}>
                                Periode - upload datum
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsDateTypePopupOpen(true)}
                                className=" cursor-pointer flex items-center gap-1 text-gray-500 hover:text-gray-700"
                            >
                                <span className={fieldLabelClasses}>
                                    {dateType === 'invoice' ? 'Periode - factuurdatum' : 'Periode - uploaddatum'}
                                </span>
                                <Cog6ToothIcon className="h-4 w-4"/>
                            </button>
                        )}
                    </div>
                    <div className="w-[234px]">
                        <DateRangeInput
                            value={dateRange}
                            onChange={handleDateRangeChange}
                        />
                    </div>
                </div>

                {hideInvoiceDateTypePicker ? null : (
                    <Popup
                        open={isDateTypePopupOpen}
                        onClose={() => setIsDateTypePopupOpen(false)}
                        onConfirm={() => setIsDateTypePopupOpen(false)}
                        title="Selecteer datum type"
                        Icon={Cog6ToothIcon}
                        confirmButtonDisplayLabel="Sluiten"
                        disableCancelButton={true}
                    >
                        <div className="py-2">
                            <div className="flex items-center mb-3">
                                <input
                                    id="invoice-date"
                                    type="radio"
                                    name="date-type"
                                    value="invoice"
                                    checked={dateType === 'invoice'}
                                    onChange={() => handleDateTypeChange('invoice')}
                                    className="h-4 w-4 text-primary focus:ring-primary"
                                />
                                <label htmlFor="invoice-date" className="ml-2 block text-sm font-medium text-gray-700">
                                    Periode - factuurdatum
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    id="upload-date"
                                    type="radio"
                                    name="date-type"
                                    value="upload"
                                    checked={dateType === 'created'}
                                    onChange={() => handleDateTypeChange('created')}
                                    className="h-4 w-4 text-primary focus:ring-primary"
                                />
                                <label htmlFor="upload-date" className="ml-2 block text-sm font-medium text-gray-700">
                                    Periode - uploaddatum
                                </label>
                            </div>
                        </div>
                    </Popup>
                )}

                <div>
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Relatie
                    </label>
                    <MultiSelectDropdown
                        options={filteredRelations}
                        selectedValues={selectedRelations}
                        onSelectionChange={(values) => { setSelectedRelations(values); setIsDirty(true); }}
                        placeholder="Alle relaties"
                        displayValue={(value) => value}
                    />
                </div>
                <div className="min-w-[22rem]">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Tags
                    </label>
                    <div className={cn(fieldVariants({size: "multi"}), "flex items-stretch")}>
                        <div className="relative flex items-center">
                            <select
                                value={selectedTagsMode}
                                onChange={(e) => { setSelectedTagsMode(e.target.value as FilterTags); setIsDirty(true); }}
                                className="appearance-none bg-transparent px-3 py-1.5 text-sm text-gray-900 focus:outline-none min-w-20"
                                aria-label="Tag filter mode"
                            >
                                <option value="is">Is</option>
                                <option value="is_not">Is niet</option>
                            </select>
                            <ChevronDownIcon className="pointer-events-none absolute right-2 h-4 w-4 text-gray-600"/>
                        </div>
                        <div className="my-1 w-px bg-gray-200"/>
                        <div className="flex-1 min-w-0 px-1">
                            <InlineMultiSelect
                                options={availableTags}
                                selectedValues={selectedTags}
                                onSelectionChange={(values) => { setSelectedTags(values); setIsDirty(true); }}
                                placeholder="Alle tags"
                                displayValue={(value) => {
                                    const tag = availableTags.find(t => t.id === value);
                                    return tag?.name || value;
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="w-44">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Documenttype
                    </label>
                    <Dropdown
                        selected={DOCUMENT_TYPE_FILTER_OPTIONS.find(o => o.value === selectedDocumentType) ?? DOCUMENT_TYPE_FILTER_OPTIONS[0]}
                        options={DOCUMENT_TYPE_FILTER_OPTIONS}
                        onUpdateFunc={(value) => { setSelectedDocumentType(value as DocumentTypeFilterEnum); setIsDirty(true); }}
                    />
                </div>
            </div>
            <div className="flex items-center justify-end gap-3">
                <FilterPendingBadge show={isDirty}/>
                <SecondaryButton title={"Wissen"} onClick={handleClear}/>
                <PrimaryButton title={"Toepassen"} onClick={handleSubmit}/>
            </div>

        </div>
    );
}
